import type { Timestamp } from "firebase/firestore";
import { Bookmark, Globe, Link2, Loader2, Smartphone } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  EditLinkDialog,
  type EditLinkFormState,
} from "src/components/dynamicLinks/editLink";
import { actorFromAuth } from "src/lib/activityService";
import { promptAndSaveDraft } from "src/lib/draftsStore";
import {
  createDynamicLink,
  deleteDynamicLink,
  deleteExpiredOwnedLinks,
  listMyDynamicLinks,
  normalizeSlugInput,
  timestampToDatetimeLocalValue,
  updateDynamicLink,
} from "src/lib/dynamicLinksService";
import {
  canManageService,
  canReadService,
  canWriteService,
  isAdmin,
  ServiceDomain,
  UserRole,
} from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import type {
  DynamicLinkListItem,
  DynamicLinkStatus,
} from "src/types/dynamicLinks";
import { DynamicLinksListSection } from "../components/dynamicLinks/DynamicLinksListSection";
import { DateTimePicker } from "../components/ui/DateTimePicker";
import { DraftsSection } from "../components/ui/DraftsSection";
import { Input } from "../components/ui/Input";
import { SlabButton } from "../components/ui/SlabButton";
import Switch from "../components/ui/Switch";
import { Constants } from "../constant";

interface DynamicLinksDraftValues {
  name: string;
  androidUrl: string;
  iosUrl: string;
  fallbackUrl: string;
  slug: string;
  status: DynamicLinkStatus;
  expiresAtLocal: string;
}

const DRAFTS_KEY = "dynamic-links:create";

const duo = {
  blue: "#1CB0F6",
} as const;

const DynamicLinksPanel: React.FC = () => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const actor = actorFromAuth(user, userProfile);

  const [links, setLinks] = useState<DynamicLinkListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [androidUrl, setAndroidUrl] = useState("");
  const [iosUrl, setIosUrl] = useState("");
  const [fallbackUrl, setFallbackUrl] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState<DynamicLinkStatus>("active");
  const [expiresAtLocal, setExpiresAtLocal] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const [edit, setEdit] = useState<EditLinkFormState | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [deleteBusySlug, setDeleteBusySlug] = useState<string | null>(null);

  const role = userProfile?.role ?? UserRole.VIEWER;
  const canReadLinks = canReadService(role, ServiceDomain.DynamicLinks);
  const canWriteLinks = canWriteService(role, ServiceDomain.DynamicLinks);
  const canStaffManageLinks = canManageService(
    role,
    ServiceDomain.DynamicLinks,
  );

  const load = useCallback(async () => {
    if (!user?.uid) return;
    const listAsAdmin = isAdmin(role) || canStaffManageLinks;
    setLoading(true);
    setError(null);
    try {
      await deleteExpiredOwnedLinks(user.uid);
      const rows = await listMyDynamicLinks(user.uid, listAsAdmin);
      setLinks(rows);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load links");
    } finally {
      setLoading(false);
    }
  }, [user?.uid, role, canStaffManageLinks]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  const handleCopy = async (shortUrl: string, slugKey: string) => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopiedSlug(slugKey);
      setTimeout(() => setCopiedSlug(null), 2000);
    } catch {
      setError("Could not copy to clipboard.");
    }
  };

  const toggleExpandSlug = (slug: string) => {
    setExpandedSlug((prev) => (prev === slug ? null : slug));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.uid) return;
    setFormError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const creatorDisplayName =
        userProfile?.displayName?.trim() ||
        user.displayName?.trim() ||
        user.email ||
        user.uid;

      const { shortUrl: createdUrl } = await createDynamicLink(
        {
          name: name.trim() || undefined,
          androidUrl,
          iosUrl,
          fallbackUrl,
          slug: slug.trim() ? normalizeSlugInput(slug) : undefined,
          status,
          expiresAtLocal: expiresAtLocal || undefined,
          creatorDisplayName,
        },
        user.uid,
        actor,
      );
      setSuccess(`Nice! Your link is live: ${createdUrl}`);
      setName("");
      setAndroidUrl("");
      setIosUrl("");
      setFallbackUrl("");
      setSlug("");
      setStatus("active");
      setExpiresAtLocal("");
      await load();
    } catch (err: unknown) {
      console.error(err);
      setFormError(
        err instanceof Error ? err.message : "Failed to create link",
      );
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row: DynamicLinkListItem) => {
    setEditError(null);
    const ex = row.expiresAt;
    let expiresLocal = "";
    if (ex && typeof (ex as Timestamp).toDate === "function") {
      expiresLocal = timestampToDatetimeLocalValue(ex as Timestamp);
    }
    setEdit({
      slug: row.slug,
      name: row.name ?? "",
      androidUrl: row.androidUrl,
      iosUrl: row.iosUrl,
      fallbackUrl: row.fallbackUrl,
      status: row.status,
      expiresAtLocal: expiresLocal,
    });
  };

  const closeEdit = () => {
    setEdit(null);
    setEditError(null);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!edit || !user?.uid) return;
    setEditError(null);
    setEditSaving(true);
    try {
      await updateDynamicLink(
        edit.slug,
        user.uid,
        {
          name: edit.name,
          androidUrl: edit.androidUrl,
          iosUrl: edit.iosUrl,
          fallbackUrl: edit.fallbackUrl,
          status: edit.status,
          expiresAtLocal: edit.expiresAtLocal || undefined,
        },
        actor,
      );
      setSuccess("Link updated.");
      closeEdit();
      await load();
    } catch (err: unknown) {
      setEditError(
        err instanceof Error ? err.message : "Could not update link",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const draftValues: DynamicLinksDraftValues = {
    name,
    androidUrl,
    iosUrl,
    fallbackUrl,
    slug,
    status,
    expiresAtLocal,
  };

  const isDraftEmpty = (v: DynamicLinksDraftValues) =>
    !v.name.trim() &&
    !v.androidUrl.trim() &&
    !v.iosUrl.trim() &&
    !v.fallbackUrl.trim() &&
    !v.slug.trim() &&
    !v.expiresAtLocal.trim();

  const suggestDraftName = (v: DynamicLinksDraftValues) => {
    const base =
      v.name.trim() ||
      v.slug.trim() ||
      v.androidUrl.trim() ||
      v.iosUrl.trim() ||
      v.fallbackUrl.trim();
    return base ? base.slice(0, 60) : "Untitled draft";
  };

  const loadDraftValues = (v: DynamicLinksDraftValues) => {
    setName(v.name ?? "");
    setAndroidUrl(v.androidUrl ?? "");
    setIosUrl(v.iosUrl ?? "");
    setFallbackUrl(v.fallbackUrl ?? "");
    setSlug(v.slug ?? "");
    setStatus(v.status === "paused" ? "paused" : "active");
    setExpiresAtLocal(v.expiresAtLocal ?? "");
    setFormError(null);
    setSuccess(`Draft "${suggestDraftName(v)}" loaded into the form.`);
  };

  const handleSaveDraft = () => {
    setFormError(null);
    setSuccess(null);
    if (isDraftEmpty(draftValues)) {
      setFormError("Fill in at least one field before saving a draft.");
      return;
    }
    const result = promptAndSaveDraft<DynamicLinksDraftValues>(
      DRAFTS_KEY,
      draftValues,
      { suggestedName: suggestDraftName(draftValues) },
    );
    if (result.cancelled) return;
    if (!result.ok) {
      setFormError(result.error ?? "Could not save draft");
      return;
    }
    setSuccess(`Saved draft "${result.draft?.name}".`);
  };

  const handleDeleteFromEdit = async () => {
    if (!edit || !user?.uid) return;
    const slug = edit.slug;
    const ok = window.confirm(
      `Delete link /${slug}? This cannot be undone. Short URLs will stop working.`,
    );
    if (!ok) return;
    setError(null);
    setEditError(null);
    setDeleteBusySlug(slug);
    try {
      await deleteDynamicLink(slug, user.uid, actor);
      setSuccess("Link deleted.");
      closeEdit();
      setExpandedSlug((s) => (s === slug ? null : s));
      await load();
    } catch (err: unknown) {
      setEditError(
        err instanceof Error ? err.message : "Could not delete link",
      );
    } finally {
      setDeleteBusySlug(null);
    }
  };

  const styles = {
    error: "border-[#FF4B4B] bg-[#FFEDED]",
    success: "border-[#58CC02] bg-[#E8F8E0]",
  } as const;

  const labelClass =
    "flex flex-col gap-2 text-sm font-extrabold uppercase tracking-wide text-[#777777]";

  if (!userProfile || !canReadLinks) {
    return (
      <div
        className="mx-auto max-w-lg rounded-[2rem] border-4 border-[#E5E5E5] bg-white p-8 text-center shadow-[0_8px_0_#E5E5E5] sm:p-10"
        style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
      >
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FFEDED] text-3xl">
          🔒
        </div>
        <h1 className="mb-2 text-2xl font-extrabold text-[#3C3C3C] sm:text-3xl">
          Almost there!
        </h1>
        <p className="text-base font-semibold leading-relaxed text-[#777777]">
          You don&apos;t have access to smart links yet. Ask an admin if you
          think this is a mistake.
        </p>
      </div>
    );
  }

  const errorOrSuccessComponent = (error: string, isError: boolean = false) => {
    return (
      <div
        className={`mb-6 rounded-2xl border-2 ${
          isError ? styles.error : styles.success
        } p-4 shadow-[0_4px_0_#E0A0A0]`}
        role="alert"
      >
        <p className="text-center text-sm font-extrabold text-[#3C3C3C]">
          {error}
        </p>
      </div>
    );
  };

  const SavingCreatingComponent = () => {
    return saving ? (
      <>
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        Creating…
      </>
    ) : (
      <>
        Create link
        <span
          className="hidden text-sm font-extrabold normal-case text-white/90 sm:inline"
          aria-hidden
        >
          — you got this!
        </span>
      </>
    );
  };

  return (
    <div
      className="mx-auto max-w-3xl pb-10 pt-2 sm:max-w-4xl sm:pt-4 lg:max-w-6xl xl:max-w-7xl"
      style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
    >
      <header className="mb-8 text-center sm:mb-10">
        <h1 className="mb-2 flex items-center justify-center gap-2 text-3xl font-extrabold tracking-tight text-white drop-shadow-sm sm:text-4xl">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#58CC02] shadow-[0_4px_0_#3F9600] sm:h-14 sm:w-14">
            <Link2
              className="h-7 w-7 text-white sm:h-8 sm:w-8"
              strokeWidth={2.5}
            />
          </span>
          <span className="max-w-[14ch] leading-tight sm:max-w-none">
            {Constants.DYNAMIC_LINKS_TITLE}
          </span>
        </h1>
        <p className="mx-auto max-w-md text-base font-bold text-white/80 sm:text-lg">
          {Constants.DYNAMIC_LINKS_DESCRIPTION}
        </p>
      </header>

      {error && errorOrSuccessComponent(error, true)}

      {success && errorOrSuccessComponent(success)}

      {edit && (
        <EditLinkDialog
          edit={edit}
          setEdit={setEdit}
          editError={editError}
          editSaving={editSaving}
          deleteBusySlug={deleteBusySlug}
          labelClass={labelClass}
          onClose={closeEdit}
          onSubmit={handleEditSave}
          onDelete={handleDeleteFromEdit}
        />
      )}

      {/*
        Layout:
        - Mobile (default): stack — form, drafts, links list (current order).
        - Desktop (lg+): two columns. Form spans the left column across both
          rows; drafts top-right; existing links bottom-right.
      */}
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:items-start lg:gap-x-8 lg:gap-y-3">
        {/* Create — step cards */}
        {canWriteLinks && (
          <section
            className="order-1 rounded-[1.75rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:p-8 lg:order-none lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:mb-0"
            aria-labelledby="create-link-heading"
          >
            <div className="mb-6 flex items-start gap-3">
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-extrabold text-white shadow-[0_4px_0_#1899D6]"
                style={{ backgroundColor: duo.blue }}
              >
                1
              </span>
              <div>
                <h2
                  id="create-link-heading"
                  className="text-xl font-extrabold text-[#3C3C3C] sm:text-2xl"
                >
                  {Constants.DYNAMIC_LINKS_WHERE_DESCRIPTION}
                </h2>
                <p className="mt-1 font-bold text-[#777777]">
                  {Constants.DYNAMIC_LINKS_REQUIRED_DESCRIPTION}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              {formError && (
                <p className="rounded-xl bg-[#FFEDED] px-4 py-3 text-center text-sm font-extrabold text-[#FF4B4B]">
                  {formError}
                </p>
              )}

              <div className="flex flex-col gap-4">
                <label className={labelClass}>
                  <span className="flex items-center gap-2">
                    <Smartphone
                      className="h-4 w-4 text-[#58CC02]"
                      aria-hidden
                    />
                    Android
                  </span>
                  <Input
                    type="url"
                    required
                    value={androidUrl}
                    onChange={(e) => setAndroidUrl(e.target.value)}
                    placeholder={Constants.ANDROID_PLACEHOLDER}
                  />
                </label>
                <label className={labelClass}>
                  <span className="flex items-center gap-2">
                    <Smartphone
                      className="h-4 w-4 text-[#1CB0F6]"
                      aria-hidden
                    />
                    iOS
                  </span>
                  <Input
                    type="url"
                    required
                    value={iosUrl}
                    onChange={(e) => setIosUrl(e.target.value)}
                    placeholder={Constants.IOS_PLACEHOLDER}
                  />
                </label>
                <label className={labelClass}>
                  <span className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-[#FFC800]" aria-hidden />
                    Everyone else (web)
                  </span>
                  <Input
                    type="url"
                    required
                    value={fallbackUrl}
                    onChange={(e) => setFallbackUrl(e.target.value)}
                    placeholder={Constants.FALLBACK_PLACEHOLDER}
                  />
                </label>
              </div>

              <div className="h-px bg-[#E5E5E5]" />

              <div className="flex items-start gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-extrabold text-white shadow-[0_4px_0_#1899D6]"
                  style={{ backgroundColor: duo.blue }}
                >
                  2
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-extrabold text-[#3C3C3C] sm:text-xl">
                    Optional extras
                  </h3>
                  <p className="mb-4 mt-1 font-bold text-[#777777]">
                    {Constants.DYNAMIC_LINKS_OPTIONAL_EXTRAS_DESCRIPTION}
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className={labelClass}>
                      Friendly name
                      <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={200}
                        placeholder="Spring campaign"
                      />
                    </label>
                    <label className={labelClass}>
                      Custom slug
                      <Input
                        type="text"
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        className="font-mono text-sm"
                        placeholder="Leave blank = auto"
                      />
                    </label>
                    <div className="flex-col">
                      <div className="flex items-center gap-4">
                        <label className={`${labelClass} sm:items-start`}>
                          Status
                        </label>
                        <Switch
                          checked={status === "active"}
                          onChange={(next: boolean) =>
                            setStatus(next ? "active" : "paused")
                          }
                          aria-label="Toggle active state"
                        />
                      </div>
                      <div className="h-2" />
                      <span className="text-sm font-extrabold text-[#3C3C3C]">
                        {status === "active"
                          ? "Active — live now"
                          : "Paused — hold my juice"}
                      </span>
                    </div>

                    <label className={labelClass}>
                      Stops working at
                      <DateTimePicker
                        value={expiresAtLocal}
                        onChanged={({ value }) => setExpiresAtLocal(value)}
                        aria-label="Link expiry date and time"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center">
                <button
                  type="submit"
                  disabled={saving}
                  className="group relative inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border-0 bg-[#58CC02] px-6 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_6px_0_#3F9600] transition-transform active:translate-y-1 active:shadow-[0_2px_0_#3F9600] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:translate-y-0 sm:w-auto"
                >
                  <SavingCreatingComponent />
                </button>
                <SlabButton
                  variant="softOutline"
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={saving || isDraftEmpty(draftValues)}
                  title={
                    isDraftEmpty(draftValues)
                      ? "Fill in at least one field first"
                      : "Save the form's current values to a draft on this device"
                  }
                >
                  <Bookmark className="h-5 w-5" aria-hidden />
                  Save to draft
                </SlabButton>
              </div>
            </form>
          </section>
        )}

        <div className="order-2 lg:order-none lg:col-start-2 lg:row-start-1">
          <DraftsSection<DynamicLinksDraftValues>
            storageKey={DRAFTS_KEY}
            currentValues={draftValues}
            onLoad={loadDraftValues}
            suggestName={suggestDraftName}
            isEmpty={isDraftEmpty}
            showSaveButton={false}
            title="Drafts"
            subtitle="Saved on this device — load any draft back into the form on the left."
          />
        </div>

        <div className="order-3 lg:order-none lg:col-start-2 lg:row-start-2">
          <DynamicLinksListSection
            links={links}
            loading={loading}
            copiedSlug={copiedSlug}
            expandedSlug={expandedSlug}
            onOpenEdit={openEdit}
            onCopy={handleCopy}
            onToggleExpandSlug={toggleExpandSlug}
          />
        </div>
      </div>
    </div>
  );
};

export default DynamicLinksPanel;
